import { readBadgeOffer, type BadgeOffer } from "./badge/client.ts";
import { choiceMarksOf, type ChoiceMarks } from "./dialogue/choice-marks.ts";
import type { AttributeSpend, BagSortOrder, BarSlot, CompanionNamingState, EdgeViewRecord, EmoteKind, ExploredSet, FeedbackAccessState, MapPlaces, PartyPositionsRecord, PartyVitalsRecord, ToolBeltSlot } from "odyssey-shared";
import { CloseCode, DEFAULT_READING_LAYOUT, InputFlag, MessageType, PROTOCOL_VERSION, SUBPROTOCOL, TICK_MS, droppedByFreeze, encodeAct, readingLayoutOf, type DialogueCloseReason, type ReadingLayout, encodeInput, encodePvpFlagToggle, encodeRespawn, encodeTarget, encodeText, newClientView, parseTextFrame, messageInfo, type ClientView, type CombatRecord, type Direction, type EffectsRecord, type HarvestEventRecord, type HarvestListRecord, type LooksRecord, type ProjectilesRecord, type TargetEffectsRecord, type PvpStateRecord, type ProgressRecord, type PvpSwitchRecord, type PerfReport, type SnapshotRecord, type ThingEventRecord, type VitalsRecord, type TextMessage } from "odyssey-shared";
import { ItemRequestBudget } from "./bag/requests.ts";
import { readBag, type BagEvent } from "./bag/wire.ts";
import { readLoot, type LootEvent } from "./chest/wire.ts";
import { readWallet, type WalletAmounts } from "./wallet/wire.ts";
import { readAttributes, type AttributesEvent } from "./character/wire.ts";
import { readTravel, type TravelEvent } from "./travel/wire.ts";
import { emptyClientView, readHandoffText, type EdgeNearFrame, type HandoffFrame, type HandoffText } from "./handoff-wire.ts";
import { welcomeMap, type DoorLine, type SkyOverride } from "odyssey-shared";
import { readHarvestState, type HarvestStateEvent } from "./harvest/wire.ts";
import { readBar, type BarEvent } from "./bar/wire.ts";
import { readToolBelt, type ToolBeltEvent } from "./belt/wire.ts";
import { emoteMessage, readActionShown, readEmojiShown, readEmotes, type ShownEmoji } from "./emotes/wire.ts";
import { BinaryDispatch } from "./binary-frames.ts";
import type { ChatEvent, ChatRequest } from "./chat/events.ts";
import type { PartyMember } from "./party/members.ts";
import { keepaliveToken } from "./chat/ping.ts";
import type { MaintenanceNotice } from "./hud/maintenance-warning.ts";
import type { LeaveAnswer } from "./leave-desk.ts";
import type { QueueFrame } from "./queue/queue-state.ts";
import type { ModerationEvent } from "./moderation/feed.ts";
import { PerfLink } from "./perf/link.ts";
import { PageActivity } from "./page-activity.ts";

/**
 * The game's WebSocket connection (PROTOCOL.md, ADR-032): sends `hello` at the protocol version, keeps the
 * client view and the ack, sends one `input` per tick and a heartbeat on a timer that outlives the render
 * loop. An optional artificial latency delays frames both ways for netcode checks by hand.
 */

/**
 * What `welcome` told us: the player's entity, where it stands, the tick, the world version and the version of the
 * rules its map server runs (ADR-069 decision 8), and the player's own first name and family name, the family
 * empty for a dev join (ADR-067 decision 4).
 */
export interface Welcome {
  readonly handle: number;
  readonly spawn: { readonly x: number; readonly y: number };
  readonly tick: number;
  readonly worldVersion: string;
  readonly rulesVersion: string;
  readonly firstName: string;
  readonly familyName: string;
  /** The map the character stands on (PROTOCOL.md 6.91.0); `main` when the welcome names none. */
  readonly map: string;
  /** The server's wall clock in milliseconds when it sent the welcome (PROTOCOL.md 6.100.0); absent from an older server. */
  readonly serverTime?: number;
  /**
   * Present only on the welcome that follows a crossing's `handoffInitiated` (PROTOCOL.md 6.96.0, ADR-147 decision 18):
   * the stand position in the new map's frame, in sixteenths, and the last input seq the map left applied.
   */
  readonly crossing?: { readonly x: number; readonly y: number; readonly inputSeq: number };
}

/**
 * A text frame from the server as the connection acts on it: a welcome, an interaction and the layout that may go
 * before it (ADR-077), a conversation's line or close (ADR-136 decision 5), a chat frame (ADR-068), an inventory frame or a chest frame (ADR-091 decision 20), the maintenance schedule (ADR-078), the respawn offer to a dead player
 * (ADR-073's amendment "the respawn is the player's"), the shrines the player's character has discovered (ADR-096
 * decision 17), the performance monitor's offer and report (ADR-087),
 * a moderation message (ADR-056's amendment of 2026-10-04: the offer, what a GM did to the player, an alert to the
 * staff), the server's error before it closes, or a frame the connection must end on, with the close code and reason it
 * reports. A frame of another protocol version is 4000, so a page and a gateway of different versions say which
 * it was (ADR-067 decision 6); any other fault is 4002. A well-formed frame of a type this build does not know,
 * from a later server, is `other` and ignored, as PROTOCOL.md 2 has it, so a new server message needs no new
 * protocol version. `queue` is the page's place in the login queue while its `hello` waits for room (ADR-127
 * decision 5).
 */
export type ServerText =
  | { readonly kind: "welcome"; readonly welcome: Welcome }
  | HandoffText
  | { readonly kind: "interaction"; readonly interaction: Interaction }
  | { readonly kind: "dialogue"; readonly dialogue: DialogueLine }
  | { readonly kind: "dialogueClose"; readonly handle: number; readonly reason: DialogueCloseReason }
  | { readonly kind: "companionNaming"; readonly state: CompanionNamingState }
  | { readonly kind: "companionState"; readonly out: boolean; readonly name: string }
  | { readonly kind: "sky"; readonly sky: SkyOverride }
  | { readonly kind: "source"; readonly key: string; readonly lit: boolean }
  | ({ readonly kind: "readingLayout" } & HeldLayout)
  | { readonly kind: "chat"; readonly chat: ChatEvent }
  | { readonly kind: "bag"; readonly bag: BagEvent }
  | { readonly kind: "loot"; readonly loot: LootEvent }
  | { readonly kind: "wallet"; readonly amounts: WalletAmounts }
  | { readonly kind: "attributes"; readonly event: AttributesEvent }
  | { readonly kind: "travel"; readonly travel: TravelEvent }
  | { readonly kind: "harvest"; readonly harvest: HarvestStateEvent }
  | { readonly kind: "bar"; readonly bar: BarEvent }
  | { readonly kind: "toolBelt"; readonly toolBelt: ToolBeltEvent }
  | { readonly kind: "emotes"; readonly sets: readonly string[] }
  | { readonly kind: "emojiShown"; readonly emoji: ShownEmoji }
  | { readonly kind: "maintenance"; readonly notice: MaintenanceNotice }
  | { readonly kind: "queue"; readonly queue: QueueFrame }
  | { readonly kind: "leaveAnswer"; readonly answer: LeaveAnswer }
  | { readonly kind: "respawnOffer"; readonly offer: RespawnOffer }
  | { readonly kind: "shrines"; readonly shrines: Shrines }
  | { readonly kind: "mapPlaces"; readonly places: MapPlaces }
  | { readonly kind: "perfOffer"; readonly intervalMs: number }
  | { readonly kind: "feedbackOffer" }
  | { readonly kind: "staffBadgeOffer"; readonly offer: BadgeOffer }
  | { readonly kind: "feedbackAccess"; readonly state: FeedbackAccessState; readonly endsInMs: number }
  | { readonly kind: "moderation"; readonly event: ModerationEvent }
  | { readonly kind: "perfReport"; readonly report: PerfReport }
  | { readonly kind: "error"; readonly code: number; readonly message: string }
  | { readonly kind: "end"; readonly code: number; readonly reason: string }
  | { readonly kind: "other" };

/**
 * The server's `respawnOffer` (PROTOCOL.md 6): the ticks until the respawn may be asked for, counted from the tick it
 * was sent, and the points, the default first.
 */
export interface RespawnOffer {
  readonly readyTicks: number;
  readonly points: readonly { readonly kind: string; readonly name: string }[];
}

/**
 * The server's `shrines` (PROTOCOL.md 5.17, ADR-096 decision 17): the handles of this map's shrines the player's own
 * character has discovered, the whole set each time; whether a discovery caused the message; the handle of the shrine
 * just discovered, 65535 when there is none or it is no thing; and that shrine's name, empty when it has none or
 * nothing was discovered; and the XP the server granted for that discovery, 0 when it granted none (ADR-096's amendment
 * of 2026-10-05).
 */
export interface Shrines {
  readonly handles: readonly number[];
  readonly discovered: boolean;
  readonly found: number;
  readonly name: string;
  readonly xp: number;
}

/**
 * Reads a text frame from the server.
 *
 * @param text The frame.
 * @returns What the connection does with it.
 * @example
 * readServerText('{"v":1,"type":5,"code":4000,"message":"text frame"}');
 * // { kind: "end", code: 4000, reason: "protocol version" }
 */
export function readServerText(text: string): ServerText {
  const verdict = parseTextFrame(text, "serverToClient", false);
  if (verdict.kind === "unknown") {
    console.warn(`ignoring a server text frame of type ${verdict.type}, which this page does not know`);
    return { kind: "other" };
  }
  if (verdict.kind === "notReady") {
    return { kind: "end", code: CloseCode.Malformed, reason: "server frame" };
  }
  if (verdict.kind !== "ok") {
    return verdict.code === CloseCode.VersionMismatch ? { kind: "end", code: CloseCode.VersionMismatch, reason: "protocol version" } : { kind: "end", code: CloseCode.Malformed, reason: "server frame" };
  }
  const message = verdict.message;
  const chat = readChat(message);
  if (chat !== null) {
    return { kind: "chat", chat };
  }
  const bag = readBag(message);
  if (bag !== null) {
    return { kind: "bag", bag };
  }
  const loot = readLoot(message);
  if (loot !== null) {
    return { kind: "loot", loot };
  }
  const amounts = readWallet(message);
  if (amounts !== null) {
    return { kind: "wallet", amounts };
  }
  const attributes = readAttributes(message);
  if (attributes !== null) {
    return { kind: "attributes", event: attributes };
  }
  const travel = readTravel(message);
  if (travel !== null) {
    return { kind: "travel", travel };
  }
  const harvest = readHarvestState(message);
  if (harvest !== null) {
    return { kind: "harvest", harvest };
  }
  const bar = readBar(message);
  if (bar !== null) {
    return { kind: "bar", bar };
  }
  const toolBelt = readToolBelt(message);
  if (toolBelt !== null) {
    return { kind: "toolBelt", toolBelt };
  }
  const sets = readEmotes(message);
  if (sets !== null) {
    return { kind: "emotes", sets };
  }
  const emoji = readEmojiShown(message) ?? readActionShown(message);
  if (emoji !== null) {
    return { kind: "emojiShown", emoji };
  }
  if (message.type === MessageType.Welcome) {
    const spawn = message["spawn"] as { x: number; y: number };
    return { kind: "welcome", welcome: { handle: message["handle"] as number, spawn, tick: message["tick"] as number, worldVersion: message["worldVersion"] as string, rulesVersion: message["rulesVersion"] as string, firstName: message["firstName"] as string, familyName: message["familyName"] as string, map: welcomeMap(message), ...(typeof message["serverTime"] === "number" ? { serverTime: message["serverTime"] } : {}), ...(typeof message["crossing"] === "object" && message["crossing"] !== null ? { crossing: message["crossing"] as Welcome["crossing"] & object } : {}) } };
  }
  const handoff = readHandoffText(message);
  if (handoff !== null) {
    return handoff;
  }
  if (message.type === MessageType.Error) {
    return { kind: "error", code: message["code"] as number, message: message["message"] as string };
  }
  if (message.type === MessageType.Interaction) {
    return { kind: "interaction", interaction: { handle: message["handle"] as number, verb: message["verb"] as string, ok: message["ok"] as boolean, text: message["text"] as string, layout: DEFAULT_READING_LAYOUT } };
  }
  if (message.type === MessageType.Dialogue) {
    return { kind: "dialogue", dialogue: { handle: message["handle"] as number, node: message["node"] as string, speaker: message["speaker"] as number, text: message["text"] as string, choices: (message["choices"] as { text: string }[]).map((choice) => choice.text), marks: (message["choices"] as Record<string, unknown>[]).map(choiceMarksOf), tier: (message["tier"] ?? null) as DialogueTier | null, end: message["end"] as boolean } };
  }
  if (message.type === MessageType.DialogueClose) {
    return { kind: "dialogueClose", handle: message["handle"] as number, reason: message["reason"] as DialogueCloseReason };
  }
  if (message.type === MessageType.CompanionState) {
    return { kind: "companionState", out: message["out"] as boolean, name: message["name"] as string };
  }
  if (message.type === MessageType.Sky) {
    return { kind: "sky", sky: message["sky"] as SkyOverride };
  }
  if (message.type === MessageType.Source) {
    return { kind: "source", key: message["key"] as string, lit: message["lit"] as boolean };
  }
  if (message.type === MessageType.CompanionNaming) {
    return { kind: "companionNaming", state: message["state"] as CompanionNamingState };
  }
  if (message.type === MessageType.ReadingLayout) {
    return { kind: "readingLayout", handle: message["handle"] as number, layout: readingLayoutOf(message) };
  }
  if (message.type === MessageType.RespawnOffer) {
    return { kind: "respawnOffer", offer: { readyTicks: message["readyTicks"] as number, points: message["points"] as RespawnOffer["points"] } };
  }
  if (message.type === MessageType.Shrines) {
    return { kind: "shrines", shrines: { handles: message["handles"] as number[], discovered: message["discovered"] as boolean, found: message["found"] as number, name: message["name"] as string, xp: message["xp"] as number } };
  }
  if (message.type === MessageType.MapPlaces) {
    return { kind: "mapPlaces", places: { spawn: message["spawn"] as MapPlaces["spawn"], places: message["places"] as MapPlaces["places"] } };
  }
  if (message.type === MessageType.PerfOffer) {
    return { kind: "perfOffer", intervalMs: message["intervalMs"] as number };
  }
  if (message.type === MessageType.FeedbackOffer) {
    return { kind: "feedbackOffer" };
  }
  if (message.type === MessageType.StaffBadgeOffer) {
    const offer = readBadgeOffer(message);
    return offer === null ? { kind: "other" } : { kind: "staffBadgeOffer", offer };
  }
  if (message.type === MessageType.FeedbackAccess) {
    return { kind: "feedbackAccess", state: message["state"] as FeedbackAccessState, endsInMs: message["endsInMs"] as number };
  }
  if (message.type === MessageType.ModerationOffer) {
    return { kind: "moderation", event: { kind: "offer", actions: message["actions"] as string[] } };
  }
  if (message.type === MessageType.Moderation) {
    return { kind: "moderation", event: { kind: "notice", what: message["kind"] as string, reason: message["reason"] as string, endsInMs: message["endsInMs"] as number } };
  }
  if (message.type === MessageType.StaffAlert) {
    return { kind: "moderation", event: { kind: "alert", familyName: message["familyName"] as string, by: message["by"] as string, text: message["text"] as string } };
  }
  if (message.type === MessageType.PerfReport) {
    const { type: _type, ...report } = message;
    return { kind: "perfReport", report: report as unknown as PerfReport };
  }
  if (message.type === MessageType.Maintenance) {
    return { kind: "maintenance", notice: { scheduled: message["scheduled"] as boolean, startsInMs: message["startsInMs"] as number, endsInMs: message["endsInMs"] as number } };
  }
  if (message.type === MessageType.Queue) {
    return { kind: "queue", queue: { place: message["place"] as number, etaMs: message["etaMs"] as number, length: message["length"] as number } };
  }
  if (message.type === MessageType.LeaveAnswer) {
    return { kind: "leaveAnswer", answer: { lock: message["lock"] as number, stay: message["stay"] as number } };
  }
  return { kind: "other" };
}

/**
 * A chat frame as the chat panel takes it, a `pong` among them for `/ping`, or null for any other message.
 *
 * @param message A parsed server message.
 * @returns The chat event, or null.
 */
function readChat(message: Readonly<Record<string, unknown>> & { readonly type: number }): ChatEvent | null {
  switch (message.type) {
    case MessageType.Said:
      return { kind: "said", handle: message["handle"] as number, firstName: message["firstName"] as string, familyName: message["familyName"] as string, text: message["text"] as string };
    case MessageType.Posed:
      return { kind: "posed", handle: message["handle"] as number, firstName: message["firstName"] as string, familyName: message["familyName"] as string, text: message["text"] as string };
    case MessageType.NameMatches:
      return { kind: "matches", prefix: message["prefix"] as string, names: message["names"] as readonly { readonly firstName: string; readonly familyName: string }[], waitMs: message["waitMs"] as number };
    case MessageType.PartyInviteMatches:
      return { kind: "inviteMatches", prefix: message["prefix"] as string, names: message["names"] as readonly { readonly firstName: string; readonly familyName: string }[], waitMs: message["waitMs"] as number };
    case MessageType.Whispered:
      return { kind: "whispered", sent: message["sent"] as boolean, firstName: message["firstName"] as string, familyName: message["familyName"] as string, text: message["text"] as string };
    case MessageType.Blocks:
      return { kind: "blocks", families: message["families"] as readonly string[] };
    case MessageType.ChatRefused:
      return { kind: "refused", reason: message["reason"] as string, waitMs: message["waitMs"] as number };
    case MessageType.Pong:
      return { kind: "pong", t: message["t"] as number };
    case MessageType.ChannelSaid:
      return { kind: "channelSaid", channel: message["channel"] as string, firstName: message["firstName"] as string, familyName: message["familyName"] as string, text: message["text"] as string };
    case MessageType.PartySaid:
      return { kind: "partySaid", firstName: message["firstName"] as string, familyName: message["familyName"] as string, text: message["text"] as string };
    case MessageType.Party:
      return { kind: "party", members: message["members"] as readonly PartyMember[] };
    case MessageType.PartyInvited:
      return { kind: "partyInvited", invite: message["invite"] as number, firstName: message["firstName"] as string, familyName: message["familyName"] as string, members: message["members"] as number, expiresMs: message["expiresMs"] as number, flag: message["flag"] as number };
    case MessageType.PartyNotice:
      return { kind: "partyNotice", notice: message["kind"] as string, firstName: message["firstName"] as string, familyName: message["familyName"] as string, invite: message["invite"] as number };
    case MessageType.PartyRefused:
      return { kind: "partyRefused", reason: message["reason"] as string, waitMs: message["waitMs"] as number };
    case MessageType.Channels:
      return { kind: "channels", joined: message["joined"] as readonly string[], muted: message["muted"] as readonly { readonly channel: string; readonly waitMs: number }[] };
    case MessageType.ChannelMuted:
      return { kind: "channelMuted", familyName: message["familyName"] as string, channel: message["channel"] as string, minutes: message["minutes"] as number };
    case MessageType.Away:
      return { kind: "away", state: message["state"] as number };
    case MessageType.SpeakerBadge: {
      const { familyName, badge } = message;
      return typeof familyName === "string" && typeof badge === "number" ? { kind: "speakerBadge", familyName, badge } : null;
    }
    case MessageType.WhisperAway:
      return { kind: "whisperAway", firstName: message["firstName"] as string, familyName: message["familyName"] as string };
    default:
      return null;
  }
}

/**
 * Connection options. `onBag` hears the inventory frames (`bag`, `bagChanged` and `itemRefused`, ADR-091 decision 20), `onWallet` the `wallet` (ADR-124 decision 4.1) and `onLoot` the loot frames (`loot`, `lootTaken`, `lootGained` and `pickUpRefused`). `onMaintenance` hears the maintenance schedule (ADR-078 decision 6), `onFeedbackOffer` the gateway's
 * offer of both kinds of report to a staff account (ADR-081's amendments of 2026-10-03 on P1.9-T72 and of 2026-10-05), `onFeedbackAccess` that the account's feedback is paused, off or open again, `onModeration` a moderation
 * message (ADR-056's amendment of 2026-10-04), `onRespawnOffer` the respawn
 * offered to the dead player (ADR-073's amendment "the respawn is the player's"), `onChat` the chat frames the gateway sends (ADR-068), `onVitals` the player's own
 * `vitals` (ADR-074 decision 5), `onPvpState` its `pvpState` (ADR-073's amendment of 2026-09-27), `onPvpSwitch` its `pvpSwitch` (ADR-076), `onProgress` its `progress` (ADR-083), `onEffects` its `effects` (ADR-079), `onTargetEffects` its `targetEffects` (ADR-079's amendment for P2.2-T4) and `onCombat` each `combat` frame, its record reused for the next (decision 3), `onShrines` the shrines the player's character has discovered and `onThingEvent` each `thingEvent` frame, its record reused for the next (ADR-096), and `onLooks` each `looks` frame, its record reused for the next (ADR-094 decision 6). `onDiscarded` hears a snapshot that did not decode or apply, never another message.
 */
export interface ConnectionOptions {
  readonly url: string;
  readonly token: string;
  readonly contentVersion: number;
  readonly latencyMs?: number;
  /**
   * The smoothed round trip the page's connection before this one had measured, which this one reads until its own
   * first keepalive is back and replaces it, so a shot owed at a reconnect or a rejoin is landed early (ADR-095 note
   * 52); null or absent on a page that has measured none.
   */
  readonly roundTripMs?: number | null;
  readonly onWelcome: (welcome: Welcome) => void;
  /**
   * Hears `handoffInitiated` (ADR-106 decision 19): the character moves to another map on this socket. Every frame after
   * it is held, in order, until `resume`, so the page can drop the world it shows and load the next before the new
   * map's `welcome` and snapshots are read.
   */
  readonly onHandoff?: (handoff: HandoffFrame) => void;
  /**
   * Hears `edgeNear` (ADR-147 decision 13): which neighbour's edge view the page receives, and the version it runs.
   */
  readonly onEdgeNear?: (near: EdgeNearFrame) => void;
  /** Hears `doorRefused` (ADR-122 decision 2.8): why the character stands in a door instead of passing it. */
  readonly onDoorRefused?: (reason: DoorLine) => void;
  /** Hears `doorPrompt` (ADR-122 decision 2.8, PROTOCOL.md 6.93.0): the character stands on a door entered with E, or has left it. */
  readonly onDoorPrompt?: (shown: boolean, name: string) => void;
  readonly onSnapshot: (record: SnapshotRecord, view: ClientView) => void;
  readonly onInteraction?: (interaction: Interaction) => void;
  /**
   * Hears a `dialogue` (ADR-136 decision 5.3): a conversation opened or moved on to its next node.
   */
  readonly onDialogue?: (line: DialogueLine) => void;
  /**
   * Hears a `dialogueClose` from the server (ADR-136 decision 5.5): the conversation with an NPC ended, and why.
   */
  readonly onDialogueClose?: (handle: number, reason: DialogueCloseReason) => void;
  /**
   * Hears a `companionNaming` (ADR-142 decision 5.4, P2.26-T7): where the naming of the companion at the offer stands.
   */
  readonly onCompanionNaming?: (state: CompanionNamingState) => void;
  /**
   * Hears a `companionState` (ADR-142 decision 6, P2.26-T6): whether the player's companion is out, and his name, empty before he has joined.
   */
  readonly onCompanionState?: (out: boolean, name: string) => void;
  /**
   * Hears a `sky` (PROTOCOL.md 6.100.0): the override the world's sky is under.
   */
  readonly onSky?: (sky: SkyOverride) => void;
  /** Hears the server's `source` (PROTOCOL.md 6.103.0): an energy source went out or was relit. */
  readonly onSource?: (key: string, lit: boolean) => void;
  readonly onChat?: (event: ChatEvent) => void;
  readonly onRoundTrip?: (rttMs: number) => void;
  readonly onBag?: (event: BagEvent) => void;
  readonly onWallet?: (amounts: WalletAmounts) => void;
  /** The own character's `attributes` frame and `attributeRefused` (ADR-138's amendment of 2026-10-31). */
  readonly onAttributes?: (event: AttributesEvent) => void;
  /**
   * Hears the map server's `travelOffer` and `travelState` (ADR-137 decisions 8 and 12): the places the character may
   * travel to, and its channel's start, end, break or refusal.
   */
  readonly onTravel?: (event: TravelEvent) => void;
  /**
   * Hears the map server's `harvestState` (ADR-142 decision 3): a hold's start, end, break or refusal, with its reason.
   */
  readonly onHarvestState?: (event: HarvestStateEvent) => void;
  /**
   * Hears each `harvested` list of a chunk and each `harvestEvent` frame, the record reused for the next (PROTOCOL.md 5.22 and
   * 5.23).
   */
  readonly onHarvested?: (list: Readonly<HarvestListRecord>) => void;
  readonly onHarvestEvent?: (events: Readonly<HarvestEventRecord>) => void;
  /**
   * Hears the server's `bar` (P2.8-T29, ADR-121 decision 5.1): the player's bar at a join, and the answer to each
   * `barSet` taken.
   */
  readonly onBar?: (event: BarEvent) => void;
  /**
   * Hears the server's `toolBelt` (ADR-142 decision 3b): the player's tool belt at a join, and the answer to each
   * `toolBeltSet` taken.
   */
  readonly onToolBelt?: (event: ToolBeltEvent) => void;
  readonly onLoot?: (event: LootEvent) => void;
  /**
   * Hears the gateway's `emotes` (ADR-114 decision 6.2): the emote sets the account holds, which is also the gateway
   * saying it takes `emote`. Until it has come on this connection `sendEmote` sends nothing.
   */
  readonly onEmotes?: (sets: readonly string[]) => void;
  /**
   * Hears the map server's `emojiShown` and `actionShown` (ADR-114 decision 6.5 and its amendment of 2026-10-09): an
   * emoji, or an action's picture, over a character in view.
   */
  readonly onEmojiShown?: (emoji: ShownEmoji) => void;
  /**
   * Whether the gateway takes a declared client request, by message id: the catalogue's `live` unless given, which
   * a test gives to send a request this build's catalogue still holds declared.
   */
  readonly liveRequest?: (type: number) => boolean;
  readonly onVitals?: (vitals: Readonly<VitalsRecord>) => void;
  readonly onPvpState?: (pvp: Readonly<PvpStateRecord>) => void;
  readonly onPvpSwitch?: (pvpSwitch: Readonly<PvpSwitchRecord>) => void;
  readonly onProgress?: (progress: Readonly<ProgressRecord>) => void;
  readonly onEffects?: (effects: Readonly<EffectsRecord>) => void;
  readonly onTargetEffects?: (effects: Readonly<TargetEffectsRecord>) => void;
  readonly onCombat?: (combat: Readonly<CombatRecord>) => void;
  readonly onMaintenance?: (notice: MaintenanceNotice) => void;
  /**
   * Hears each `queue` frame, the page's place in the login queue while its `hello` waits for room (ADR-127 decision
   * 5), a granted page's place 1 among them, until the `welcome`.
   */
  readonly onQueue?: (queue: QueueFrame) => void;
  /** Hears each `leaveAnswer`, the gateway's answer to the menu's `leave` (ADR-084's amendment of 2026-10-13). */
  readonly onLeaveAnswer?: (answer: LeaveAnswer) => void;
  readonly onRespawnOffer?: (offer: RespawnOffer) => void;
  readonly onShrines?: (shrines: Shrines) => void;
  /** Hears each `mapPlaces`, the map's Spawn Point and the places the character has found, whole each time (ADR-100 decision 6). */
  readonly onMapPlaces?: (places: MapPlaces) => void;
  /** Hears the `explored` frame of a join, the fog cells the character has explored on its map (ADR-100 decision 22). */
  readonly onExplored?: (set: ExploredSet) => void;
  readonly onPartyVitals?: (vitals: Readonly<PartyVitalsRecord>) => void;
  readonly onPartyPositions?: (positions: Readonly<PartyPositionsRecord>) => void;
  /** Hears each `edgeView` (ADR-147 decision 13), its record valid only during the call. */
  readonly onEdgeView?: (view: Readonly<EdgeViewRecord>) => void;
  readonly onThingEvent?: (events: Readonly<ThingEventRecord>) => void;
  readonly onLooks?: (looks: Readonly<LooksRecord>) => void;
  readonly onProjectiles?: (projectiles: Readonly<ProjectilesRecord>) => void;
  readonly onFeedbackOffer?: () => void;
  /**
   * Hears the gateway's `staffBadgeOffer` (ADR-112 decision 5.5): the types a staff account may wear, the one it wears and
   * whether it is hidden, sent after `welcome` and after each change.
   */
  readonly onStaffBadgeOffer?: (offer: BadgeOffer) => void;
  readonly onFeedbackAccess?: (state: FeedbackAccessState, endsInMs: number) => void;
  readonly onModeration?: (event: ModerationEvent) => void;
  /**
   * Whether a Game Master has frozen the player now (ADR-056's amendment of 2026-10-06, decision 42), read at each
   * send: while true the connection sends no client message the shared freeze list drops but `input`
   * (`withheldWhileFrozen`). Absent, nothing is withheld.
   */
  readonly frozen?: () => boolean;
  readonly onDiscarded: () => void;
  readonly onClose: (code: number, reason: string) => void;
}

/**
 * Whether the page's sender withholds a client message while its player is frozen (ADR-056's amendment of 2026-10-06,
 * decision 42): every message the shared freeze list drops (`droppedByFreeze`), which the gateway would drop
 * unanswered, but `input`, which still goes, without a direction since the scene holds the character still, so the
 * map server keeps hearing the page. The list is the one the gateway reads, so a message put on it later is withheld
 * here from that day.
 *
 * @param type The client message's type.
 * @returns True when a frozen page does not send it.
 * @example
 * withheldWhileFrozen(MessageType.Act);
 * // true
 */
export function withheldWhileFrozen(type: number): boolean {
  return type !== MessageType.Input && droppedByFreeze(type);
}

/**
 * The outcome of an `interact` (ADR-051): the target and verb as sent, whether it happened, the
 * words to show, and how those words read when they are taller than the reading panel (ADR-077): the layout a
 * `readingLayout` named for this thing just before, or `scroll`.
 */
export interface Interaction {
  readonly handle: number;
  readonly verb: string;
  readonly ok: boolean;
  readonly text: string;
  readonly layout: ReadingLayout;
}

/**
 * One `dialogue` as the page shows it (ADR-136 decision 5.3, PROTOCOL.md 6.84.0): the NPC spoken to, the node the
 * server last sent, which a choice names back, the speaker's index in the NPC look table, the line's text, the
 * choices' texts in the order shown, numbered from 0, each choice's marks by the same index (the amendment of
 * 2026-10-28, decision 7), and whether the node ends the conversation. The line and choice ids are not kept: the page
 * shows the English the server sent.
 */
export interface DialogueLine {
  readonly handle: number;
  readonly node: string;
  readonly speaker: number;
  readonly text: string;
  readonly choices: readonly string[];
  readonly marks: readonly ChoiceMarks[];
  /** The NPC's mood tier (ADR-146 decision 8.2); null for an NPC with no tiers or a server before the field. */
  readonly tier: DialogueTier | null;
  readonly end: boolean;
}

/**
 * An NPC's mood tier as a `dialogue` carries it: the tier's word, its place among the tiers from 0, how many tiers there
 * are and, for an NPC the conversation can feed, whether today's feed is still wanted (null for one it cannot).
 */
export interface DialogueTier {
  readonly name: string;
  readonly at: number;
  readonly of: number;
  /** The next tier's word, null at the last tier. */
  readonly next: string | null;
  readonly hungry: boolean | null;
}

/**
 * What a `readingLayout` told the page (ADR-077 decision 6): the thing whose next reply it shapes, and the layout,
 * `scroll` for one this page does not know.
 */
export interface HeldLayout {
  readonly handle: number;
  readonly layout: ReadingLayout;
}

/**
 * The layout of a reply: the held one when it names the reply's thing, otherwise the default. The map server sends
 * `readingLayout` immediately before the reply it shapes, on the same socket, so the connection holds it only until
 * the next reply, whatever thing that names.
 *
 * @param held The layout held since the last reply, or null.
 * @param handle The reply's thing.
 * @returns The reply's layout.
 */
export function heldLayoutFor(held: HeldLayout | null, handle: number): ReadingLayout {
  return held !== null && held.handle === handle ? held.layout : DEFAULT_READING_LAYOUT;
}

/**
 * A connection to the gateway.
 */
export class GameConnection {
  public readonly view: ClientView = newClientView();
  /**
   * The performance monitor's side of this connection (ADR-087): bytes received, the keepalive's round trip, the
   * gateway's offer and its last report, and the way to ask for the next.
   */
  public readonly perf: PerfLink;
  /** Settles once the socket has closed, however it closed. */
  public readonly ended: Promise<void>;
  private settleEnded: () => void = () => undefined;
  private readonly socket: WebSocket;
  private readonly binary: BinaryDispatch;
  private readonly buffer = new Uint8Array(64);
  private readonly latency: number;
  private readonly heartbeat: ReturnType<typeof setInterval>;
  private joined = false;
  private closed = false;
  private heldLayout: HeldLayout | null = null;
  private heldFrames: (string | ArrayBuffer)[] | null = null;
  /**
   * The page's activity for the input's Used and Away bits (ADR-090 decision 3), null where there is no window.
   */
  private readonly itemRequests = new ItemRequestBudget();
  private readonly activity: PageActivity | null = typeof window === "undefined" ? null : new PageActivity(window, document);
  /**
   * Whether the gateway has offered idle and AFK with `away` on this connection, before which no input carries the
   * Used or Away bit, since a gateway before protocol 6.16.0 drops such an input.
   */
  private awayOffered = false;
  /**
   * Whether this connection's gateway has sent `emotes`, and so takes `emote` (ADR-114 decision 6.2).
   */
  private emotesOffered = false;

  /**
   * Opens the socket and sends `hello` once open.
   *
   * @param options Where to connect and what to do with frames.
   */
  public constructor(private readonly options: ConnectionOptions) {
    this.latency = options.latencyMs ?? 0;
    this.perf = new PerfLink((text) => {
      if (!this.ready || this.socket.readyState !== WebSocket.OPEN) {
        return false;
      }
      this.delay(() => this.socket.send(text));
      return true;
    }, options.roundTripMs ?? null, options.onRoundTrip ?? null);
    this.ended = new Promise<void>((resolve) => {
      this.settleEnded = resolve;
    });
    this.binary = new BinaryDispatch(this.view, { onSnapshot: options.onSnapshot, onVitals: (vitals) => options.onVitals?.(vitals), onPvpState: (pvp) => options.onPvpState?.(pvp), onPvpSwitch: (pvpSwitch) => options.onPvpSwitch?.(pvpSwitch), onProgress: (progress) => options.onProgress?.(progress), onEffects: (effects) => options.onEffects?.(effects), onTargetEffects: (effects) => options.onTargetEffects?.(effects), onCombat: (combat) => options.onCombat?.(combat), onThingEvent: (events) => options.onThingEvent?.(events), onHarvested: (list) => options.onHarvested?.(list), onHarvestEvent: (events) => options.onHarvestEvent?.(events), onLooks: (looks) => options.onLooks?.(looks), onProjectiles: (projectiles) => options.onProjectiles?.(projectiles), onExplored: (set) => options.onExplored?.(set), onPartyVitals: (vitals) => options.onPartyVitals?.(vitals), onPartyPositions: (positions) => options.onPartyPositions?.(positions), onEdgeView: (view) => options.onEdgeView?.(view), onDiscarded: options.onDiscarded });
    this.socket = new WebSocket(options.url, SUBPROTOCOL);
    this.socket.binaryType = "arraybuffer";
    this.socket.onopen = () => {
      this.socket.send(encodeText({ type: MessageType.Hello, zoneToken: options.token, protocol: PROTOCOL_VERSION, content: options.contentVersion, handoff: true }));
    };
    this.socket.onmessage = (event) => this.delay(() => this.receive(event.data as string | ArrayBuffer));
    this.socket.onclose = (event) => this.end(event.code, event.reason);
    this.heartbeat = setInterval(() => this.beat(), HEARTBEAT_INTERVAL_MS);
  }

  /**
   * Proves to the map server that this client is still there while the page is not drawing. Inputs go out from
   * the render loop, which the browser stops for a hidden tab, so the map server would otherwise reap a
   * backgrounded tab as a dead peer; a timer keeps firing in a hidden tab, slowly but enough. Its token is even, so
   * its `pong` is never taken for one of `/ping`'s, whose tokens are odd.
   *
   * Its `pong` also times the round trip a shot is landed early by (ADR-095 decision 17, `PerfLink.smoothedRttMs`), so
   * the first goes out as the welcome arrives, since a page that has measured none lands every shot with no lead; and
   * it waits the artificial latency as every other frame does, with its token taken before the wait, so a page run
   * with a latency measures the round trip it plays on.
   */
  private beat(): void {
    if (!this.joined || this.closed || this.socket.readyState !== WebSocket.OPEN) {
      return;
    }
    const text = encodeText({ type: MessageType.Ping, t: keepaliveToken(performance.now()) });
    this.delay(() => {
      if (!this.closed && this.socket.readyState === WebSocket.OPEN) {
        this.socket.send(text);
      }
    });
  }

  /**
   * Whether the connection can be played on: `welcome` has arrived and the connection has not ended. Once
   * it ends, nothing the player does is sent, so the message line keeps why the tab left the world.
   *
   * @returns True when joined and not ended.
   */
  public get ready(): boolean {
    return this.joined && !this.closed;
  }

  /**
   * Sends one input, carrying the page's Used and Away bits once the gateway has offered idle and AFK (ADR-090
   * decision 3), the sprint bit when the player asks to run with it (ADR-116 decision 13) and the jump bit on the one
   * input after a hop (decision 18). Each bit only asks: the map server alone says whether the step is a run step and
   * whether the hop plays.
   *
   * @param seq The predictor's seq.
   * @param dir The intent.
   * @param sprint Whether the player asks to run with this input; false unless given.
   * @param jump Whether the player asks to hop with this input; false unless given.
   */
  public sendInput(seq: number, dir: Direction, sprint = false, jump = false): void {
    if (!this.joined || this.socket.readyState !== WebSocket.OPEN) {
      return;
    }
    const flags = (this.awayOffered && this.activity !== null ? this.activity.take() : 0) | (sprint ? InputFlag.Sprint : 0) | (jump ? InputFlag.Jump : 0);
    const bytes = encodeInput(this.buffer, { seq, ackTick: this.view.tick, dir, flags });
    const frame = Uint8Array.from(this.buffer.subarray(0, bytes));
    this.delay(() => this.socket.send(frame));
  }

  /**
   * Sends one `act` (ADR-074 decision 2): an ability toward an aim after the input the page sent last. The outcome
   * arrives as a `used` or `refused` event in a `combat` frame. Nothing is sent while the player is frozen.
   *
   * @param afterSeq The seq of the last input sent before it, 0 before any.
   * @param ability The ability's number in the rules.
   * @param target The selected handle, or `NO_HANDLE`.
   * @param aim The aim in 65,536ths of a turn.
   */
  public sendAct(afterSeq: number, ability: number, target: number, aim: number): void {
    if (!this.ready || this.socket.readyState !== WebSocket.OPEN || this.withheld(MessageType.Act)) {
      return;
    }
    const bytes = encodeAct(this.buffer, { afterSeq, ability, target, aim });
    const frame = Uint8Array.from(this.buffer.subarray(0, bytes));
    this.delay(() => this.socket.send(frame));
  }

  /**
   * Sends one `pvpFlagToggle` (ADR-076 decision 1): the preference the player wants. The outcome arrives in `pvpSwitch`,
   * and the preference itself in `pvpState` once a switch ends.
   *
   * @param wanted The preference wanted, `PvpFlag.Protected` or `PvpFlag.Flagged`.
   * @returns Whether it was sent: false before the connection is ready, once its socket has closed, or while the
   *   player is frozen.
   * @throws Error when the preference is neither.
   */
  public sendFlagToggle(wanted: number): boolean {
    if (!this.ready || this.socket.readyState !== WebSocket.OPEN || this.withheld(MessageType.PvpFlagToggle)) {
      return false;
    }
    const bytes = encodePvpFlagToggle(this.buffer, { wanted });
    const frame = Uint8Array.from(this.buffer.subarray(0, bytes));
    this.delay(() => this.socket.send(frame));
    return true;
  }

  /**
   * Sends one `respawn` (ADR-073's amendment "the respawn is the player's"): the dead player asks to stand up at a point
   * of the last offer. The map server answers nothing; the snapshot and `vitals` show the respawn.
   *
   * @param point The point's index in the offer.
   * @returns Whether it was sent: false before the connection is ready or once its socket has closed.
   */
  public sendRespawn(point: number): boolean {
    if (!this.ready || this.socket.readyState !== WebSocket.OPEN) {
      return false;
    }
    const bytes = encodeRespawn(this.buffer, { point });
    const frame = Uint8Array.from(this.buffer.subarray(0, bytes));
    this.delay(() => this.socket.send(frame));
    return true;
  }

  /**
   * Sends `queueLeave` (ADR-127 decision 5): the player leaves the login queue, which ends the place the gateway
   * holds for the account; the gateway answers nothing and closes 1000. It is sent only while the connection waits
   * for its `welcome`, since the gateway closes a welcomed connection that sends it (4002), and it does not wait the
   * artificial latency, so the frame is on the socket before a close that follows it in the same task.
   *
   * @returns Whether it was sent: false once the connection is welcomed or has ended, or while its socket is not open.
   */
  public sendQueueLeave(): boolean {
    if (this.joined || this.closed || this.socket.readyState !== WebSocket.OPEN) {
      return false;
    }
    this.socket.send(encodeText({ type: MessageType.QueueLeave }));
    return true;
  }

  /**
   * Sends `leave` (ADR-084's amendment of 2026-10-13): the game menu asks what a leave now would meet, or leaves. The
   * gateway answers `leaveAnswer` and, for a confirmed leave nothing locks, closes the connection 1000. It passes a
   * freeze, as closing the tab does.
   *
   * @param confirm False to ask and change nothing, true to leave.
   * @returns Whether it was sent: false before the connection is ready or once its socket has closed.
   */
  public sendLeave(confirm: boolean): boolean {
    if (!this.ready || this.socket.readyState !== WebSocket.OPEN) {
      return false;
    }
    const text = encodeText({ type: MessageType.Leave, confirm });
    this.delay(() => {
      if (!this.closed && this.socket.readyState === WebSocket.OPEN) {
        this.socket.send(text);
      }
    });
    return true;
  }

  /**
   * Sends one `target` (ADR-034's amendment of 2026-10-01): the entity the player selects, `NO_HANDLE` to clear. The map
   * server answers nothing; it judges the request against the view this page was sent.
   *
   * @param handle The handle, `NO_HANDLE` to clear.
   * @returns Whether it was sent: false before the connection is ready, once its socket has closed, or while the
   *   player is frozen.
   */
  public sendTarget(handle: number): boolean {
    if (!this.ready || this.socket.readyState !== WebSocket.OPEN || this.withheld(MessageType.Target)) {
      return false;
    }
    const bytes = encodeTarget(this.buffer, { handle });
    const frame = Uint8Array.from(this.buffer.subarray(0, bytes));
    this.delay(() => this.socket.send(frame));
    return true;
  }

  /**
   * Sends one `interact`: a verb aimed at an entity handle. The reply arrives through `onInteraction`;
   * a verb the server drops gets no reply. Nothing is sent while the player is frozen.
   *
   * @param handle Target entity handle.
   * @param verb Verb name.
   * @throws Error when the verb is not a well-formed name.
   */
  public sendInteract(handle: number, verb: string): void {
    if (!this.joined || this.socket.readyState !== WebSocket.OPEN || this.withheld(MessageType.Interact)) {
      return;
    }
    const frame = encodeText({ type: MessageType.Interact, handle, verb });
    this.delay(() => this.socket.send(frame));
  }

  /**
   * Sends one `dialogueChoose` (ADR-136 decision 5.3): the choice, by its place among those shown, at the node the
   * server last sent. Nothing is sent while the player is frozen, since the map server drops a frozen account's choice.
   *
   * @param handle The NPC spoken to.
   * @param node The node the choice was shown at.
   * @param choice The choice's index among those shown, 0 to 5.
   * @returns Whether it was sent.
   */
  public sendDialogueChoose(handle: number, node: string, choice: number): boolean {
    if (!this.ready || this.socket.readyState !== WebSocket.OPEN || this.withheld(MessageType.DialogueChoose)) {
      return false;
    }
    const frame = encodeText({ type: MessageType.DialogueChoose, handle, node, choice });
    this.delay(() => this.socket.send(frame));
    return true;
  }

  /**
   * Sends one `travelAsk` (ADR-137 decision 7): the world map's Travel button asks for the places the character may
   * travel to. The map server answers with a `travelOffer` from the place the character stands in, or from anywhere,
   * or a `travelState` refusal.
   *
   * @returns Whether it was sent: false before the connection is ready, once its socket has closed, or while frozen.
   */
  public sendTravelAsk(): boolean {
    return this.sendTravelText({ type: MessageType.TravelAsk, from: "anywhere" });
  }

  /**
   * Sends one `travel` (ADR-137 decision 9): the player chose a place of the last offer, by its serial and the place's
   * index. The map server answers with a `travelState`.
   *
   * @param serial The offer's serial.
   * @param index The place's index in the offer.
   * @returns Whether it was sent.
   */
  public sendTravel(serial: number, index: number): boolean {
    return this.sendTravelText({ type: MessageType.Travel, serial, index });
  }

  /**
   * Sends one `travelCancel` (ADR-137 decision 10): the player ends the channel that is running, which the map server
   * answers with `travelState` `broken` `cancelled`. A frozen player's cancel is sent, since it only ends a channel.
   *
   * @returns Whether it was sent.
   */
  public sendTravelCancel(): boolean {
    if (!this.ready || this.socket.readyState !== WebSocket.OPEN) {
      return false;
    }
    const frame = encodeText({ type: MessageType.TravelCancel });
    this.delay(() => this.socket.send(frame));
    return true;
  }

  /**
   * Sends a request a held character's page withholds (`withheld`): an attribute spend or respec, as `sendTravelText`
   * sends a travel request.
   *
   * @param message The request.
   * @returns Whether it was sent.
   */
  private sendRequestText(message: TextMessage): boolean {
    return this.sendTravelText(message);
  }

  /**
   * Sends a travel request unless the connection is not ready or the request is withheld while frozen.
   *
   * @param message The request.
   * @returns Whether it was sent.
   */
  private sendTravelText(message: TextMessage): boolean {
    if (!this.ready || this.socket.readyState !== WebSocket.OPEN || this.withheld(message.type)) {
      return false;
    }
    const frame = encodeText(message);
    this.delay(() => this.socket.send(frame));
    return true;
  }

  /**
   * Sends one `harvest` (ADR-142 decision 3): the player asks to harvest the prop with this index on the map. The map
   * server answers with a `harvestState`. A frozen player's request is not sent.
   *
   * @param index The prop's index.
   * @returns Whether it was sent: false before the connection is ready, once its socket has closed, or while frozen.
   */
  public sendHarvest(index: number): boolean {
    return this.sendTravelText({ type: MessageType.Harvest, index });
  }

  /**
   * Sends one `companionName` (ADR-142 decision 5.4, P2.26-T7): the name the player gives the companion at the offer,
   * which the map server answers with a `companionNaming`. A frozen player's name is not sent.
   *
   * @param name The name, by the first-name rule.
   * @returns Whether it was sent: false before the connection is ready, once its socket has closed, or while frozen.
   */
  public sendCompanionName(name: string): boolean {
    return this.sendTravelText({ type: MessageType.CompanionName, name });
  }

  /**
   * Sends one `companionSet` (ADR-142 decision 6.2, P2.26-T6): send the companion out or home, which the map server answers
   * with a `companionState` when it takes it. A frozen player's request is not sent.
   *
   * @param out True to send him out, false home.
   * @returns Whether it was sent: false before the connection is ready, once its socket has closed, or while frozen.
   */
  public sendCompanionSet(out: boolean): boolean {
    return this.sendTravelText({ type: MessageType.CompanionSet, out });
  }

  /**
   * Sends one `spendAttributes` (ADR-138's amendment of 2026-10-31, decision 4): the window's confirmed choice, names and
   * counts only, which the map server answers with an `attributes` frame or an `attributeRefused`. Not sent while frozen.
   *
   * @param spends The batch.
   * @returns Whether it was sent: false before the connection is ready, once its socket has closed, or while frozen.
   */
  public sendSpendAttributes(spends: readonly AttributeSpend[]): boolean {
    return this.sendRequestText({ type: MessageType.SpendAttributes, spends });
  }

  /**
   * Sends one `respecAttributes` (decision 7), answered as `sendSpendAttributes` is. Not sent while frozen.
   *
   * @returns Whether it was sent.
   */
  public sendRespecAttributes(): boolean {
    return this.sendRequestText({ type: MessageType.RespecAttributes });
  }

  /**
   * Sends one `dialogueClose` (ADR-136 decision 5.3): the player closed the conversation with an NPC, which the map
   * server answers with nothing. A frozen player's close is sent, since it only closes a window opened before.
   *
   * @param handle The NPC spoken to.
   * @returns Whether it was sent.
   */
  public sendDialogueClose(handle: number): boolean {
    if (!this.ready || this.socket.readyState !== WebSocket.OPEN) {
      return false;
    }
    const frame = encodeText({ type: MessageType.DialogueClose, handle, reason: "done" });
    this.delay(() => this.socket.send(frame));
    return true;
  }

  /**
   * Sends one `useItem` (ADR-091 decisions 10 and 13): the slot and the item the page expects there, which the map
   * server reads against its own copy and never trusts. The answer is `bagChanged` or `itemRefused`. Nothing is sent
   * while the catalogue holds the request declared and not live, since a gateway drops such a frame unread and no
   * answer would ever clear the slot's pending mark; the row that handles the request sets it live. Nothing is sent
   * past the page's own budget of item requests a second either (`bag/requests.ts`), since the map server drops a
   * request over its rate unanswered.
   *
   * @param slot The bag slot.
   * @param item The item's rules id.
   * @returns Whether it was sent: false before the connection is ready, once its socket has closed, while the
   *   request is not live, while the player is frozen, or past the budget.
   * @throws Error when the slot or the item is out of the message's schema, which the bag store's own state rules out.
   */
  public sendUseItem(slot: number, item: string): boolean {
    const live = this.options.liveRequest?.(MessageType.UseItem) ?? messageInfo(MessageType.UseItem)?.live === true;
    if (!this.ready || this.socket.readyState !== WebSocket.OPEN || !live || this.withheld(MessageType.UseItem) || !this.itemRequests.take(performance.now())) {
      return false;
    }
    const frame = encodeText({ type: MessageType.UseItem, slot, item });
    this.delay(() => this.socket.send(frame));
    return true;
  }

  /**
   * Sends one `bagSplit` (P2.8-T14, ADR-091's note of the author's controls): the slot split, which the map server reads
   * against its own copy and never trusts. The answer is `bagChanged` with the cause `split`, or `itemRefused` for the
   * request `split`. Nothing is sent as for a move: before the connection is ready, while the request is not live,
   * while the player is frozen, or past the page's budget of item requests.
   *
   * @param slot The slot split.
   * @returns Whether it was sent.
   * @throws Error when the slot is out of the message's schema, which the bag store's own state rules out.
   */
  public sendBagSplit(slot: number): boolean {
    return this.sendItemRequest(MessageType.BagSplit, { slot });
  }

  /**
   * Sends one `destroyItem` (P2.8-T14, ADR-091's note of the author's controls): the slot, the item the page holds there
   * and the count the player confirmed, which the map server reads against its own copy, destroying the stack only when
   * it still holds that item in that count. The answer is `bagChanged` with the cause `destroy`, or `itemRefused` for the request `destroy`. Nothing is
   * sent as for a move.
   *
   * @param slot The slot.
   * @param item The item the page holds there.
   * @param quantity The count the player confirmed.
   * @returns Whether it was sent.
   * @throws Error when a field is out of the message's schema, which the bag store's own state rules out.
   */
  public sendDestroyItem(slot: number, item: string, quantity: number): boolean {
    return this.sendItemRequest(MessageType.DestroyItem, { slot, item, quantity });
  }

  /**
   * Sends one `dropItem` (P2.8-T34, ADR-091's amendment for P2.8-T34): the slot, the item the page holds there and its
   * whole count, which the map server reads against its own copy, dropping the stack onto the ground only when it still
   * holds that item in that count. The answer is `bagChanged` with the cause `drop`, or `itemRefused` for the request
   * `drop`. Nothing is sent as for a move.
   *
   * @param slot The slot.
   * @param item The item the page holds there.
   * @param quantity The count the page holds there.
   * @returns Whether it was sent.
   * @throws Error when a field is out of the message's schema, which the bag store's own state rules out.
   */
  public sendDropItem(slot: number, item: string, quantity: number): boolean {
    return this.sendItemRequest(MessageType.DropItem, { slot, item, quantity });
  }

  /**
   * Sends one `bagSort` (P2.8-T48, ADR-125 decision 3.4): the order, which the map server reads against its own copy of
   * the bag. The answer is `bagChanged` with the cause `sort`, or `itemRefused` for the request `sort`. Nothing is sent
   * as for a move.
   *
   * @param by The order.
   * @returns Whether it was sent.
   * @throws Error when the order is out of the message's schema, which its type rules out.
   */
  public sendBagSort(by: BagSortOrder): boolean {
    return this.sendItemRequest(MessageType.BagSort, { by });
  }

  /**
   * Sends one `bagKeep` (P2.8-T49, ADR-125 decision 6.4): the slot and whether to keep its stack in place, which the map
   * server reads against its own copy of the bag. The answer is `bagKept` with the kept slots, or `itemRefused` for the
   * request `keep`. Nothing is sent as for a move.
   *
   * @param slot The slot.
   * @param kept Whether to keep it in place.
   * @returns Whether it was sent.
   * @throws Error when the slot is out of the message's schema, which the bag store's own state rules out.
   */
  public sendBagKeep(slot: number, kept: boolean): boolean {
    return this.sendItemRequest(MessageType.BagKeep, { slot, kept });
  }

  /**
   * Sends one `barSet` (P2.8-T29, ADR-121 decision 5.1): the whole bar the player arranged under its seq, or null for
   * Reset. Nothing is sent before the connection is ready, once its socket has closed, while the catalogue holds the
   * message not live, or while the player is frozen. The map server counts it on a window of its own, which the page's
   * one set in flight never reaches, so it takes nothing of the item requests' budget.
   *
   * @param seq The set's seq, 1 to 65535.
   * @param slots The ten slots, or null for the default bar.
   * @returns Whether it was sent.
   * @throws Error when a slot is out of the message's schema, which the page's bar rules out.
   */
  public sendBarSet(seq: number, slots: readonly BarSlot[] | null): boolean {
    const live = this.options.liveRequest?.(MessageType.BarSet) ?? messageInfo(MessageType.BarSet)?.live === true;
    if (!this.ready || this.socket.readyState !== WebSocket.OPEN || !live || this.withheld(MessageType.BarSet)) {
      return false;
    }
    const frame = encodeText({ type: MessageType.BarSet, seq, slots });
    this.delay(() => this.socket.send(frame));
    return true;
  }

  /**
   * Sends one `toolBeltSet` (ADR-142 decision 3b): the whole belt the player arranged and its selection under a seq, on the
   * same terms as a `barSet`: nothing is sent before the connection is ready, once its socket has closed, while the
   * catalogue holds the message not live, or while the player is frozen. The map server counts it on a window of its own,
   * which the page's one set in flight never reaches.
   *
   * @param seq The set's seq, 1 to 65535.
   * @param slots The four slots.
   * @param selected The selection, 0 to 4.
   * @returns Whether it was sent.
   * @throws Error when a slot is out of the message's schema, which the page's belt rules out.
   */
  public sendToolBeltSet(seq: number, slots: readonly ToolBeltSlot[], selected: number): boolean {
    const live = this.options.liveRequest?.(MessageType.ToolBeltSet) ?? messageInfo(MessageType.ToolBeltSet)?.live === true;
    if (!this.ready || this.socket.readyState !== WebSocket.OPEN || !live || this.withheld(MessageType.ToolBeltSet)) {
      return false;
    }
    const frame = encodeText({ type: MessageType.ToolBeltSet, seq, slots, selected });
    this.delay(() => this.socket.send(frame));
    return true;
  }

  /**
   * Sends one item request other than a use: refused before the connection is ready, once its socket has closed, while
   * the catalogue holds it not live, while the player is frozen, or past the page's budget of item requests.
   *
   * @param type The request's type.
   * @param fields Its fields.
   * @returns Whether it was sent.
   * @throws Error when a field is out of the message's schema.
   */
  private sendItemRequest(type: typeof MessageType.BagSplit | typeof MessageType.DestroyItem | typeof MessageType.DropItem | typeof MessageType.BagSort | typeof MessageType.BagKeep, fields: { readonly slot?: number; readonly item?: string; readonly quantity?: number; readonly by?: BagSortOrder; readonly kept?: boolean }): boolean {
    const live = this.options.liveRequest?.(type) ?? messageInfo(type)?.live === true;
    if (!this.ready || this.socket.readyState !== WebSocket.OPEN || !live || this.withheld(type)) {
      return false;
    }
    const frame = encodeText({ type, ...fields });
    if (!this.itemRequests.take(performance.now())) {
      return false;
    }
    this.delay(() => this.socket.send(frame));
    return true;
  }

  /**
   * Sends one `bagMove` (P2.8-T14, ADR-091 decision 4): the slot moved from and the slot moved onto, which the map server
   * reads against its own copy and never trusts. The answer is `bagChanged` with the cause `move`, or `itemRefused` for
   * the request `move`. Nothing is sent while the catalogue holds the request declared and not live, while the player
   * is frozen, or past the page's own budget of item requests a second (`bag/requests.ts`), which `useItem`, `bagMove`
   * and `lootTake` share as they share the map server's window.
   *
   * @param from The slot moved from.
   * @param to The slot moved onto.
   * @returns Whether it was sent: false before the connection is ready, once its socket has closed, while the
   *   request is not live, while the player is frozen, or past the budget.
   * @throws Error when a slot is out of the message's schema, which the bag store's own state rules out.
   */
  public sendBagMove(from: number, to: number): boolean {
    const live = this.options.liveRequest?.(MessageType.BagMove) ?? messageInfo(MessageType.BagMove)?.live === true;
    if (!this.ready || this.socket.readyState !== WebSocket.OPEN || !live || this.withheld(MessageType.BagMove)) {
      return false;
    }
    const frame = encodeText({ type: MessageType.BagMove, from, to });
    if (!this.itemRequests.take(performance.now())) {
      return false;
    }
    this.delay(() => this.socket.send(frame));
    return true;
  }

  /**
   * Sends one `lootTake` (ADR-091 decisions 19 and 20): the chest's handle and the lines asked for, one for Take and
   * every line still to take for Take all, so either is a single frame. The map server decides each line and answers
   * with one `lootTaken`, after `bagChanged` when anything moved. Nothing is sent while the catalogue holds the
   * request declared and not live, nor past the page's own budget of item requests a second (`bag/requests.ts`),
   * which `useItem` and `lootTake` share as they share the map server's window, since a request over that rate is
   * dropped unanswered.
   *
   * @param handle The chest's handle.
   * @param lines The lines asked for: 1 to 8 distinct line numbers.
   * @returns Whether it was sent: false before the connection is ready, once its socket has closed, while the
   *   request is not live, while the player is frozen, or past the budget.
   * @throws Error when the handle or the lines are out of the message's schema, which the chest store's own state
   *   rules out.
   */
  public sendLootTake(handle: number, lines: readonly number[]): boolean {
    const live = this.options.liveRequest?.(MessageType.LootTake) ?? messageInfo(MessageType.LootTake)?.live === true;
    if (!this.ready || this.socket.readyState !== WebSocket.OPEN || !live || this.withheld(MessageType.LootTake)) {
      return false;
    }
    const frame = encodeText({ type: MessageType.LootTake, handle, lines: [...lines] });
    if (!this.itemRequests.take(performance.now())) {
      return false;
    }
    this.delay(() => this.socket.send(frame));
    return true;
  }

  /**
   * Sends one `pickUp` (P2.8-T64, ADR-091's amendment for P2.8-T13, decision 3): the handle of a ground item to pick
   * up, which the map server reads against its own ground items, view, reach and bag. The answer is `bagChanged` and
   * `lootGained` once the pick-up commits, or `pickUpRefused`. It is counted on the item request budget, which every
   * item request shares as they share the map server's window, since a request over that rate is dropped unanswered.
   *
   * @param handle The ground item's handle.
   * @returns Whether it was sent: false before the connection is ready, once its socket has closed, while the request
   *   is not live, while the player is frozen, or past the budget.
   * @throws Error when the handle is out of the message's schema, which a handle from the client's view rules out.
   */
  public sendPickUp(handle: number): boolean {
    const live = this.options.liveRequest?.(MessageType.PickUp) ?? messageInfo(MessageType.PickUp)?.live === true;
    if (!this.ready || this.socket.readyState !== WebSocket.OPEN || !live || this.withheld(MessageType.PickUp)) {
      return false;
    }
    const frame = encodeText({ type: MessageType.PickUp, handle });
    if (!this.itemRequests.take(performance.now())) {
      return false;
    }
    this.delay(() => this.socket.send(frame));
    return true;
  }

  /**
   * Sends one `emote` (ADR-114 decision 6.1): an emote's kind and id and, for an action, the handle of the target it
   * names or `NO_HANDLE`. The gateway judges the id, the account's sets and the limits, and the map server the
   * target; an action is answered by `posed` and a refusal by `chatRefused`. Nothing is sent until this connection's
   * gateway has sent `emotes`, since a gateway that does not know the message closes on it (decision 6.2), nor while
   * the player is frozen, since the shared freeze list drops it (decision 6.7).
   *
   * @param kind The emote's kind.
   * @param id Its id within its kind.
   * @param target The entity handle an action names, or `NO_HANDLE`.
   * @returns Whether it was sent: false before the connection is ready, once its socket has closed, before the
   *   gateway offered emotes, while the request is not live, or while the player is frozen.
   * @throws Error when the id or the target is out of the message's schema, which the wheel's own state rules out.
   */
  public sendEmote(kind: EmoteKind, id: number, target: number): boolean {
    const live = this.options.liveRequest?.(MessageType.Emote) ?? messageInfo(MessageType.Emote)?.live === true;
    if (!this.ready || this.socket.readyState !== WebSocket.OPEN || !this.emotesOffered || !live || this.withheld(MessageType.Emote)) {
      return false;
    }
    const frame = encodeText(emoteMessage(kind, id, target));
    this.delay(() => this.socket.send(frame));
    return true;
  }

  /**
   * Sends a chat request (ADR-068 and its amendments "chat commands, Discord style" and "`/ping`"): a Local line,
   * an action, a whisper, a block, an unblock, a name lookup, one of `/ping`'s pings, or `/afk` (ADR-090 decision 5),
   * which is sent only once the gateway has offered it and forgets the presses that typed it.
   *
   * @param request The request, which the page has checked against the chat text rule and the names' rules.
   * @returns False when the connection is not joined and open, so nothing was sent.
   * @throws Error when the request is out of its message's schema, which the page's checks rule out.
   */
  public sendChat(request: ChatRequest): boolean {
    if (!this.ready || this.socket.readyState !== WebSocket.OPEN) {
      return false;
    }
    if (request.kind === "afk") {
      if (!this.awayOffered) {
        return false;
      }
      this.activity?.forget();
    }
    const frame = encodeText(chatMessage(request));
    this.delay(() => this.socket.send(frame));
    return true;
  }

  /**
   * Holds every frame that arrives from now until `resume`, for a page that must stand the next map before it reads them.
   * A hold that is already on stays as it is.
   */
  public hold(): void {
    this.heldFrames ??= [];
  }

  /**
   * Ends a handoff's hold: the client view is emptied, as a join's is, and every frame held since `handoffInitiated` is
   * read in the order it arrived, the new map's `welcome` first. A frame read here that begins another handoff holds
   * the rest again, as every frame arriving in a hold is. A seamless crossing keeps the view (`keepView`), since the map
   * left is still drawn until the welcome, whose handler empties it (ADR-147 decision 18).
   *
   * @param keepView True to leave the client view as it is.
   */
  public resume(keepView = false): void {
    const held = this.heldFrames;
    if (held === null) {
      return;
    }
    this.heldFrames = null;
    if (!keepView) {
      emptyClientView(this.view);
    }
    this.heldLayout = null;
    for (const frame of held) {
      this.receive(frame);
    }
  }

  /**
   * Whether a handoff holds this connection's frames.
   *
   * @returns True from `handoffInitiated` until `resume`.
   */
  public get handingOff(): boolean {
    return this.heldFrames !== null;
  }

  /**
   * Closes the socket.
   */
  public close(): void {
    this.socket.close(1000, "leaving");
  }

  /**
   * Whether a client message is withheld now because a Game Master has frozen the player (`withheldWhileFrozen`).
   *
   * @param type The client message's type.
   * @returns True when it must not be sent.
   */
  private withheld(type: number): boolean {
    return withheldWhileFrozen(type) && this.options.frozen?.() === true;
  }

  /**
   * Runs an action now or after the artificial latency.
   *
   * @param action The action.
   */
  private delay(action: () => void): void {
    if (this.latency > 0) {
      setTimeout(action, this.latency);
    } else {
      action();
    }
  }

  /**
   * Handles one frame. A text frame the connection cannot read ends it, closing the socket with the code it
   * reports: 4000 for a server of another protocol version, 4002 for any other fault. A binary frame is read by its
   * type (`BinaryDispatch`, ADR-074 decision 11).
   *
   * @param data Text or bytes.
   */
  private receive(data: string | ArrayBuffer): void {
    if (this.heldFrames !== null) {
      this.heldFrames.push(data);
      return;
    }
    this.perf.count(typeof data === "string" ? data.length : data.byteLength);
    if (typeof data === "string") {
      const read = readServerText(data);
      if (read.kind === "end") {
        this.socket.close(read.code, read.reason);
        this.end(read.code, read.reason);
      } else if (read.kind === "welcome") {
        this.joined = true;
        this.beat();
        this.options.onWelcome(read.welcome);
      } else if (read.kind === "handoff") {
        this.heldFrames = [];
        this.options.onHandoff?.(read.handoff);
      } else if (read.kind === "edgeNear") {
        this.options.onEdgeNear?.(read);
      } else if (read.kind === "doorRefused") {
        this.options.onDoorRefused?.(read.reason);
      } else if (read.kind === "doorPrompt") {
        this.options.onDoorPrompt?.(read.shown, read.name);
      } else if (read.kind === "error") {
        this.end(read.code, read.message);
      } else if (read.kind === "readingLayout") {
        this.heldLayout = { handle: read.handle, layout: read.layout };
      } else if (read.kind === "dialogue") {
        this.options.onDialogue?.(read.dialogue);
      } else if (read.kind === "dialogueClose") {
        this.options.onDialogueClose?.(read.handle, read.reason);
      } else if (read.kind === "companionNaming") {
        this.options.onCompanionNaming?.(read.state);
      } else if (read.kind === "companionState") {
        this.options.onCompanionState?.(read.out, read.name);
      } else if (read.kind === "sky") {
        this.options.onSky?.(read.sky);
      } else if (read.kind === "source") {
        this.options.onSource?.(read.key, read.lit);
      } else if (read.kind === "interaction") {
        const layout = heldLayoutFor(this.heldLayout, read.interaction.handle);
        this.heldLayout = null;
        this.options.onInteraction?.({ ...read.interaction, layout });
      } else if (read.kind === "chat") {
        if (read.chat.kind === "pong") {
          this.perf.pong(read.chat.t, performance.now());
        } else if (read.chat.kind === "away") {
          this.awayOffered = true;
        }
        this.options.onChat?.(read.chat);
      } else if (read.kind === "bag") {
        this.options.onBag?.(read.bag);
      } else if (read.kind === "wallet") {
        this.options.onWallet?.(read.amounts);
      } else if (read.kind === "attributes") {
        this.options.onAttributes?.(read.event);
      } else if (read.kind === "travel") {
        this.options.onTravel?.(read.travel);
      } else if (read.kind === "harvest") {
        this.options.onHarvestState?.(read.harvest);
      } else if (read.kind === "bar") {
        this.options.onBar?.(read.bar);
      } else if (read.kind === "toolBelt") {
        this.options.onToolBelt?.(read.toolBelt);
      } else if (read.kind === "loot") {
        this.options.onLoot?.(read.loot);
      } else if (read.kind === "emotes") {
        this.emotesOffered = true;
        this.options.onEmotes?.(read.sets);
      } else if (read.kind === "emojiShown") {
        this.options.onEmojiShown?.(read.emoji);
      } else if (read.kind === "maintenance") {
        this.options.onMaintenance?.(read.notice);
      } else if (read.kind === "queue") {
        this.options.onQueue?.(read.queue);
      } else if (read.kind === "leaveAnswer") {
        this.options.onLeaveAnswer?.(read.answer);
      } else if (read.kind === "respawnOffer") {
        this.options.onRespawnOffer?.(read.offer);
      } else if (read.kind === "shrines") {
        this.options.onShrines?.(read.shrines);
      } else if (read.kind === "mapPlaces") {
        this.options.onMapPlaces?.(read.places);
      } else if (read.kind === "feedbackOffer") {
        this.options.onFeedbackOffer?.();
      } else if (read.kind === "staffBadgeOffer") {
        this.options.onStaffBadgeOffer?.(read.offer);
      } else if (read.kind === "feedbackAccess") {
        this.options.onFeedbackAccess?.(read.state, read.endsInMs);
      } else if (read.kind === "moderation") {
        this.options.onModeration?.(read.event);
      } else if (read.kind === "perfOffer") {
        this.perf.offered(read.intervalMs);
      } else if (read.kind === "perfReport") {
        this.perf.reported(read.report, performance.now());
      }
      return;
    }
    this.binary.receive(new Uint8Array(data));
  }

  /**
   * Reports the end of the connection once, whichever of the server's error message or the socket close
   * arrives first.
   *
   * @param code Close code.
   * @param reason Reason text.
   */
  private end(code: number, reason: string): void {
    if (this.closed) {
      return;
    }
    this.closed = true;
    clearInterval(this.heartbeat);
    this.activity?.detach();
    this.settleEnded();
    this.options.onClose(code, reason);
  }
}

/**
 * A chat request as its message.
 *
 * @param request The request.
 * @returns The message's type and fields.
 */
export function chatMessage(request: ChatRequest): { readonly type: MessageType } & Readonly<Record<string, unknown>> {
  switch (request.kind) {
    case "say":
      return { type: MessageType.Say, text: request.text };
    case "pose":
      return { type: MessageType.Pose, text: request.text };
    case "lookup":
      return { type: MessageType.NameLookup, prefix: request.prefix };
    case "inviteLookup":
      return { type: MessageType.PartyInviteLookup, prefix: request.prefix };
    case "whisper":
      return { type: MessageType.Whisper, firstName: request.firstName, familyName: request.familyName, text: request.text };
    case "block":
      return { type: MessageType.Block, familyName: request.familyName };
    case "unblock":
      return { type: MessageType.Unblock, familyName: request.familyName };
    case "ping":
      return { type: MessageType.Ping, t: request.t };
    case "channelSay":
      return { type: MessageType.ChannelSay, channel: request.channel, text: request.text };
    case "channelJoin":
      return { type: MessageType.ChannelJoin, channel: request.channel };
    case "channelLeave":
      return { type: MessageType.ChannelLeave, channel: request.channel };
    case "partySay":
      return { type: MessageType.PartySay, text: request.text };
    case "afk":
      return { type: MessageType.Afk };
    case "partyInvite":
      return { type: MessageType.PartyInvite, firstName: request.firstName, familyName: request.familyName };
    case "partyAnswer":
      return { type: MessageType.PartyAnswer, invite: request.invite, accept: request.accept };
    case "partyLeave":
      return { type: MessageType.PartyLeave };
    case "partyRemove":
      return { type: MessageType.PartyRemove, firstName: request.firstName, familyName: request.familyName };
    case "partyPromote":
      return { type: MessageType.PartyPromote, firstName: request.firstName, familyName: request.familyName };
    case "partyGrant":
      return { type: MessageType.PartyGrant, firstName: request.firstName, familyName: request.familyName, mayInvite: request.mayInvite };
    case "channelMute":
      return { type: MessageType.ChannelMute, familyName: request.familyName, channel: request.channel, minutes: request.minutes };
  }
}

/**
 * Milliseconds between inputs: one per simulation tick.
 */
export const INPUT_INTERVAL_MS = TICK_MS;

/**
 * Milliseconds between heartbeats (P1.4-T11). Ten seconds is far inside the map server's ninety second
 * liveness timeout even when a hidden tab's timers are held to one wake a minute.
 */
export const HEARTBEAT_INTERVAL_MS = 10_000;
